import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Search, ArrowRight, Shield, Truck, Clock, ChevronRight, Package, Camera, Wrench, Baby, Palmtree, Gamepad2, PartyPopper, Laptop } from "lucide-react";
import { Button } from "@/components/ui/button";
import ItemCard from "@/components/ItemCard";
import AdBanner from "@/components/AdBanner";
import SEOHead, { 
  generateLocalBusinessSchema, 
  generateBreadcrumbSchema, 
  generateItemListSchema 
} from "@/components/SEOHead";
import { CATEGORIES, US_STATES, getLocationByStateCity } from "@/lib/constants";
import { getCitySEOContent, SEO_BORROW_CATEGORIES } from "@/lib/citySEO";
import api from "@/lib/api";

export default function CityPage() {
  const { stateSlug, citySlug } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const state = US_STATES[stateSlug];
  const location = getLocationByStateCity(stateSlug, citySlug);

  useEffect(() => {
    if (!location) return;
    api.get("/items", { params: { location: location.name, limit: 50 } })
      .then((res) => setItems(res.data.items))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [stateSlug, citySlug, location]);

  if (!location || !state) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading font-bold text-2xl text-stone-900 mb-2">Location Not Found</h1>
        <p className="text-stone-500 mb-6">We couldn't find this city.</p>
        <Link to="/"><Button className="rounded-full bg-coral hover:bg-coral text-white">Back to Home</Button></Link>
      </div>
    );
  }

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/browse?location=${encodeURIComponent(location.name)}&search=${encodeURIComponent(searchQuery)}`);
  };

  const itemCategories = [...new Set(items.map((i) => i.category))];
  const cityContent = getCitySEOContent(location.cityName, location.stateAbbrev, location.stateName);

  // --- SEO Data ---
  const pageTitle = `Borrow Items in ${location.cityName}, ${location.stateAbbrev} | Paradise Borrowed\u2122`;
  const pageDescription = `Borrow beach gear, baby equipment, cameras, tools & more from neighbors in ${location.cityName}, ${location.stateName}. Paradise Borrowed\u2122 \u2014 the peer-to-peer borrowing platform. Join free.`;
  const canonicalUrl = `https://www.paradiseborrowed.com/${stateSlug}/${citySlug}`;

  // --- JSON-LD Structured Data (per spec) ---
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": `Borrow Items in ${location.cityName}, ${location.stateAbbrev}`,
        "description": pageDescription,
        "url": canonicalUrl,
        "isPartOf": {
          "@type": "WebSite",
          "name": "Paradise Borrowed",
          "url": "https://www.paradiseborrowed.com"
        },
        "about": {
          "@type": "Place",
          "name": `${location.cityName}, ${location.stateName}`,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": location.cityName,
            "addressRegion": location.stateAbbrev,
            "addressCountry": "US"
          }
        }
      },
      generateBreadcrumbSchema([
        { name: "Home", url: "https://www.paradiseborrowed.com" },
        { name: state.name, url: `https://www.paradiseborrowed.com/${stateSlug}` },
        { name: location.cityName, url: canonicalUrl }
      ]),
      generateLocalBusinessSchema({
        cityName: location.cityName,
        stateName: location.stateName,
        stateAbbrev: location.stateAbbrev,
        stateSlug,
        citySlug
      }),
      ...(items.length > 0 ? [generateItemListSchema(items, `${location.cityName}, ${location.stateAbbrev}`)] : [])
    ]
  };

  // Sibling cities for internal linking (up to 5, excluding current)
  const siblingCities = state.cities.filter(c => c.slug !== citySlug).slice(0, 5);

  return (
    <div data-testid="city-page">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schema={schema}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-50 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm text-stone-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-coral">Home</Link>
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
            <Link to={`/${stateSlug}`} className="hover:text-coral">{state.name}</Link>
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
            <span className="text-stone-900" aria-current="page">{location.cityName}</span>
          </nav>
        </div>
      </div>

      {/* Hero — h1 per spec */}
      <section className="bg-gradient-to-br from-stone-50 via-orange-50/30 to-stone-50 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-stone-500 mb-3">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-medium">{location.cityName}, {location.stateName}</span>
          </div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-stone-900 leading-tight" data-testid="city-headline">
            Borrow Items from Your Neighbors in {location.cityName}, {location.stateAbbrev}
          </h1>
          <p className="mt-4 text-base text-stone-600 leading-relaxed max-w-2xl">
            {cityContent.intro}
          </p>
          <p className="mt-2 text-sm text-stone-500 leading-relaxed max-w-2xl">
            {cityContent.geo}
          </p>
          <form onSubmit={handleSearch} className="mt-8 max-w-lg" data-testid="city-search-form">
            <div className="flex items-center bg-white rounded-full border border-stone-200 p-1.5 shadow-sm hover:shadow-md transition-shadow">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input type="text" placeholder={`Search items to borrow in ${location.cityName}...`}
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-full text-sm bg-transparent focus:outline-none"
                  data-testid="city-search-input" 
                  aria-label={`Search items to borrow in ${location.cityName}`}
                />
              </div>
              <Button type="submit" className="rounded-full px-6 h-10 bg-coral hover:bg-coral text-white" data-testid="city-search-btn">
                Search
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Ad Banner */}
      <div className="py-4 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner size="responsive" position="top-leaderboard" />
        </div>
      </div>

      {/* h2: What You Can Borrow */}
      <section className="py-12 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900 mb-6">
            What You Can Borrow in {location.cityName}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SEO_BORROW_CATEGORIES.map((cat) => {
              const hasItems = itemCategories.some(ic => cat.name.includes(ic.split(" ")[0]) || ic.includes(cat.name.split(" ")[0]));
              return (
                <Link key={cat.name}
                  to={`/browse?location=${encodeURIComponent(location.name)}&search=${encodeURIComponent(cat.name)}`}
                  className={`p-4 rounded-xl border text-left transition-all group ${
                    hasItems ? "bg-white border-stone-200 hover:border-coral hover:shadow-sm" : "bg-stone-50 border-stone-100"
                  }`}
                  data-testid={`city-category-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className={`font-medium text-sm mb-1 group-hover:text-coral transition-colors ${hasItems ? "text-stone-900" : "text-stone-500"}`}>{cat.name}</p>
                  <p className="text-xs text-stone-400">{cat.desc}</p>
                </Link>
              );
            })}
          </div>
          {/* Also show active categories as quick links */}
          {itemCategories.length > 0 && (
            <div className="flex gap-2 mt-4 flex-wrap">
              <span className="text-xs text-stone-400 self-center">Active:</span>
              {CATEGORIES.filter(c => itemCategories.includes(c.name)).map((cat) => {
                const Icon = cat.icon;
                return (
                  <Link key={cat.name}
                    to={`/browse?location=${encodeURIComponent(location.name)}&category=${encodeURIComponent(cat.name)}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-coral/30 bg-coral-light/50 text-xs text-coral font-medium hover:border-coral transition-all"
                  >
                    <Icon className="w-3 h-3" />{cat.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Available Items */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900">
              Available Items in {location.cityName}
            </h2>
            <Link to={`/browse?location=${encodeURIComponent(location.name)}`}
              className="text-coral text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-city-items">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex gap-8">
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="rounded-2xl border border-stone-200 bg-white overflow-hidden animate-pulse">
                      <div className="aspect-[4/3] bg-stone-100" />
                      <div className="p-4 space-y-2"><div className="h-3 bg-stone-100 rounded w-1/2" /><div className="h-4 bg-stone-100 rounded w-3/4" /></div>
                    </div>
                  ))}
                </div>
              ) : items.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((item) => <ItemCard key={item.id} item={item} />)}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
                  <p className="text-stone-500 text-lg mb-2">No items listed in {location.cityName} yet</p>
                  <p className="text-stone-400 text-sm mb-4">Be the first lender in this area!</p>
                  <Link to="/create-listing">
                    <Button className="rounded-full bg-coral hover:bg-coral text-white" data-testid="list-first-item">List an Item</Button>
                  </Link>
                </div>
              )}
            </div>
            <div className="hidden xl:block w-[300px] shrink-0">
              <div className="sticky top-24">
                <AdBanner size="rectangle" position="sidebar" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Banner - Mid Content (Mobile) */}
      <div className="py-6 bg-stone-50 xl:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner size="banner" position="mid-content-mobile" />
        </div>
      </div>

      {/* h2: How Borrowing Works */}
      <section className="py-12 sm:py-16 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900 mb-8">
              How Borrowing Works
            </h2>
            <div className="space-y-0">
              {[
                { num: "1", title: `Browse items listed by neighbors in ${location.cityName}`, desc: `Search by category or keyword to find what you need. Every listing includes photos, condition details, and verified lender reviews. Filter by price, availability, and pickup or delivery options in the ${location.cityName} area.` },
                { num: "2", title: "Book the dates you need", desc: "Select your borrowing dates, review the total cost (including any applicable platform fees), and confirm your booking. Pay securely through Stripe. The lender receives your request instantly." },
                { num: "3", title: "Pick up, use, and return", desc: "Coordinate pickup or delivery with the lender through our in-app messaging. Use the item for your project, trip, or event. Return it in the same condition, confirm the return in the app, and you\u2019re done." },
              ].map((step, i) => (
                <div key={step.num} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-coral text-white flex items-center justify-center text-sm font-semibold shrink-0">{step.num}</div>
                    {i < 2 && <div className="w-px flex-1 bg-stone-200 my-2"></div>}
                  </div>
                  <div className={i < 2 ? "pb-8" : ""}>
                    <p className="font-medium text-stone-900 mb-1">{step.title}</p>
                    <p className="text-sm text-stone-500 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* h2: Why Borrow Instead of Buy? */}
      <section className="py-12 sm:py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900 mb-4">
              Why Borrow Instead of Buy?
            </h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-2">
              The borrowers economy is simple: access what you need, when you need it, without the cost and waste of buying new. In {location.cityName}, your neighbors already own the items you're looking for — a power drill, a baby car seat, a paddleboard, a camera lens.
            </p>
            <p className="text-stone-600 text-sm leading-relaxed mb-8">
              Peer-to-peer borrowing through Paradise Borrowed saves you money, reduces waste, and builds community. Lenders earn from items that would otherwise sit idle. Borrowers get what they need at a fraction of the purchase price. Everyone wins.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Truck, title: "Local Pickup or Delivery", desc: `Most items in ${location.cityName} are available for local pickup. Many lenders offer delivery right to your door.` },
                { icon: Shield, title: "Trusted Lenders", desc: "Every lender is reviewed and rated by previous borrowers. Verified profiles and in-app messaging keep every transaction transparent." },
                { icon: Clock, title: "Flexible Borrowing", desc: "Borrow for a day, a week, or longer. Choose the dates that work for you and only pay for the time you need." },
              ].map((card) => (
                <div key={card.title} className="p-5 rounded-2xl bg-white border border-stone-200">
                  <card.icon className="w-6 h-6 text-coral mb-3" />
                  <h3 className="font-heading font-semibold text-sm text-stone-900 mb-1">{card.title}</h3>
                  <p className="text-xs text-stone-500 leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Searches — borrow-only terminology */}
      <section className="py-12 border-t border-stone-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading font-semibold text-base text-stone-900 mb-4">
            Popular Searches in {location.cityName}
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              `borrow items ${location.cityName}`,
              `borrow tools ${location.cityName}`,
              `borrow baby gear ${location.cityName}`,
              `borrow camera ${location.cityName}`,
              `lend items ${location.cityName}`,
              `borrow beach gear ${location.cityName}`,
              `borrow electronics ${location.cityName}`,
              `neighbor lending ${location.cityName}`,
            ].map((term) => (
              <Link key={term}
                to={`/browse?location=${encodeURIComponent(location.name)}&search=${encodeURIComponent(term.split(" ").slice(1, 3).join(" "))}`}
                className="text-xs px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200 text-stone-600 hover:border-coral hover:text-coral transition-all"
              >
                {term}
              </Link>
            ))}
            {CATEGORIES.slice(0, 6).map((cat) => (
              <Link key={cat.name}
                to={`/browse?location=${encodeURIComponent(location.name)}&category=${encodeURIComponent(cat.name)}`}
                className="text-xs px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200 text-stone-600 hover:border-coral hover:text-coral transition-all"
              >
                {cat.name} in {location.cityName}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Ad Banner */}
      <div className="py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner size="responsive" position="pre-cities" />
        </div>
      </div>

      {/* Internal Linking: Other Cities in State (descriptive anchor text) */}
      <section className="py-12 sm:py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-semibold text-xl sm:text-2xl text-stone-900 mb-2">
            Borrow Items in Other {state.name} Cities
          </h2>
          <p className="text-sm text-stone-500 mb-6">
            Paradise Borrowed is available across {state.name}. Browse items in nearby cities:
          </p>
          <div className="flex flex-wrap gap-3">
            {state.cities.filter(c => c.slug !== citySlug).slice(0, 12).map((city) => (
              <Link key={city.slug}
                to={`/${stateSlug}/${city.slug}`}
                className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-stone-700 hover:border-coral hover:text-coral transition-all"
              >
                Borrow items in {city.name}, {location.stateAbbrev}
              </Link>
            ))}
            {state.cities.length > 13 && (
              <Link
                to={`/${stateSlug}`}
                className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-coral font-medium hover:bg-coral-light transition-all"
              >
                View all {state.name} cities
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* h2: CTA — Start Borrowing */}
      <section className="py-16 bg-gradient-to-br from-orange-50 via-amber-50 to-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-stone-900 mb-3">
            Start Borrowing in {location.cityName}, {location.stateAbbrev}
          </h2>
          <p className="text-stone-600 text-sm max-w-md mx-auto mb-6">
            Join Paradise Borrowed for free. Browse items from your neighbors or list your own to start earning.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to={`/browse?location=${encodeURIComponent(location.name)}`}>
              <Button size="lg" className="rounded-full px-8 bg-coral hover:bg-coral text-white btn-press" data-testid="city-cta-browse">
                Browse Items in {location.cityName}
              </Button>
            </Link>
            <Link to="/create-listing">
              <Button size="lg" variant="outline" className="rounded-full px-8 border-stone-300 text-stone-700 hover:border-coral hover:text-coral" data-testid="city-cta-lend">
                Start Lending
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
